import { SlideOverHeader, SlideOverWrapper } from '@/components/slideOvers_v2';
import { BaseAudience } from '@/models/audiences';
import { ISlideOverContentProps } from '@/models/slideOver';
import { FC, PropsWithChildren } from 'react';
import AudiencesIcon from '../../../../../assets/icons/audiences-people.svg?react';

import { useTranslation } from 'react-i18next';
import { useGetAudienceQuery, useGetEstimateQuery } from '@/services/endpoints/audiences';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useDelayedBoolean } from '@/hooks/useDelayedBoolean';
import { AudienceDetailSlideOverContent } from './AudienceDetailSlideOverContent';
import { SpinnerIcon } from '@/components/icons';
import { Button } from '@/lib';

type AudienceDetailSlideOverProps = PropsWithChildren<
  ISlideOverContentProps & {
    audienceId: BaseAudience['id'];
  }
>;

export const AudienceDetailSlideOver: FC<AudienceDetailSlideOverProps> = ({ audienceId }) => {
  const { t } = useTranslation('audience_details');

  const workspace = useWorkspace();
  const {
    isFetching,
    data: audience,
    isUninitialized,
  } = useGetAudienceQuery({
    workspaceId: workspace.id,
    audienceId,
  });

  const { isFetching: isFetchingEstimation, data: estimation } = useGetEstimateQuery({
    workspaceId: workspace.id,
    audienceId,
  });

  const isFetchingDelayed = useDelayedBoolean(isFetching, 400);
  const shouldShowLoading = isFetching || isFetchingDelayed || isUninitialized;

  if (shouldShowLoading) {
    return (
      <SlideOverWrapper>
        <div className='flex grow items-center justify-center'>
          <SpinnerIcon className='size-10' loading />
        </div>
      </SlideOverWrapper>
    );
  }

  const handleAudienceDetailClick = () => {
    window.open(`/audiences/${audience.id}`, '_blank');
  };

  const renderSubtitle = () => {
    return (
      <>
        <span className='text-brand-600'>{isFetchingEstimation ? <SpinnerIcon className='inline-block size-4' loading /> : estimation.size}</span>{' '}
        {t('slideover.total_users')}
      </>
    );
  };

  return (
    <SlideOverWrapper>
      <SlideOverHeader icon={<AudiencesIcon className='size-16' />} title={audience.name} subtitle={renderSubtitle()} />

      <AudienceDetailSlideOverContent audience={audience} />

      <div className='flex items-center justify-center py-11'>
        <Button intent='secondary' size='sm' onClick={handleAudienceDetailClick}>
          {t('slideover.view_detail')}
        </Button>
      </div>
    </SlideOverWrapper>
  );
};
